import api from '../../services/api';

/*
 * The stock sync of a Shopify store with the WMS. A store is its
 * SettingEcommerce (`settingId`). Whoever reaches its warehouse, and the
 * client that owns it, read and change it; aligning the stock is only for
 * whoever stores in that warehouse. A store that fails answers 502 with what it said.
 */

const channelPath = (settingId) => `ecommerces/shopify/${settingId}/`;

/**
 * The stores in reach, with how their sync stands. Paginated. Nothing here
 * calls the stores.
 *
 * @param {{warehouse?: number, company?: number, search?: string, page?: number, page_size?: number}} params
 */
export const getStockSyncChannelsRequest = (params) =>
  api.get('ecommerces/shopify/stock-sync/', { params });

/** Asks the store what it granted only when Ninja did not keep it. */
export const getStockSyncRequest = (settingId) => api.get(`${channelPath(settingId)}stock-sync/`);

/**
 * Only the fields sent change. Turning it on, or moving the location while
 * on, pushes every linked product.
 *
 * @param {number} settingId
 * @param {{enabled?: boolean, location_id?: string, initial_load_slot_id?: number|null}} payload
 */
export const updateStockSyncRequest = (settingId, payload) =>
  api.patch(`${channelPath(settingId)}stock-sync/`, payload);

/** The store's own locations, read from Shopify: `{results: [{id, name, active}]}`. */
export const getStoreLocationsRequest = (settingId) => api.get(`${channelPath(settingId)}locations/`);

/** The last catalog sync's report, or `null`. */
export const getCatalogSyncRequest = (settingId) => api.get(`${channelPath(settingId)}catalog-sync/`);

/** 202 with the queued report. One at a time per store. */
export const startCatalogSyncRequest = (settingId) => api.post(`${channelPath(settingId)}catalog-sync/`);

/** The last comparison of Ninja's stock with the store's, with its rows once done; or `null`. */
export const getStockPreviewRequest = (settingId) => api.get(`${channelPath(settingId)}stock-alignment/preview/`);

/** 202 with the queued comparison. It only reads, and voids the one before. */
export const startStockPreviewRequest = (settingId) =>
  api.post(`${channelPath(settingId)}stock-alignment/preview/`);

/** The last alignment's report, or `null`. */
export const getStockAlignmentRequest = (settingId) => api.get(`${channelPath(settingId)}stock-alignment/`);

/**
 * Applies the chosen rows of the last comparison, once: 202 with the queued
 * report. What it receives goes to the store's `initial_load_slot`.
 *
 * @param {number} settingId
 * @param {{preview_id: string, listing_ids: number[]}} payload - `preview_id` is the comparison's `result.token`.
 */
export const applyStockAlignmentRequest = (settingId, payload) =>
  api.post(`${channelPath(settingId)}stock-alignment/`, payload);

/**
 * The products linked to the store, by SKU. Paginated.
 *
 * @param {number} settingId
 * @param {{search?: string, status?: string, page?: number, page_size?: number}} params
 */
export const getListingsRequest = (settingId, params) =>
  api.get(`${channelPath(settingId)}listings/`, { params });

/** Pauses one product, or resumes it, which pushes it. Answers the row. */
export const updateListingRequest = (settingId, listingId, payload) =>
  api.patch(`${channelPath(settingId)}listings/${listingId}/`, payload);

/** Pushes one product now: 202 with the row and `queued`. */
export const syncListingRequest = (settingId, listingId) =>
  api.post(`${channelPath(settingId)}listings/${listingId}/sync/`);
